/**
 * REF-6 — the segmented refund reason catalogue.
 *
 * The two groups are never merged into one ranked list. An item reason is a SERVICE
 * QUALITY signal ("we got the order wrong"); a fee reason is a CONTROL signal ("someone
 * is handing back tax"). Summing them would let a control problem hide inside a quality
 * problem, which is the whole point of the split.
 *
 * The same catalogue backs the REF-2 Refunds page and the Sales Report's Refund Analysis
 * card, so the two can never drift apart.
 */
import type { RefundReasonGroup } from './refundModel';

/**
 * A refund can post with no reason captured — same convention the Cancelled Orders log
 * uses for a blank `reason`, so a reader sees one consistent label across both surfaces
 * rather than a blank cell in one and "No Reason" in the other.
 */
export const NO_REASON = 'No Reason';

/** Existing set — carried over unchanged from the Cancel/Refund reasons already shipped. */
export const ITEM_REFUND_REASONS = [
    'Late preparation',
    'Missing item',
    'Items not available',
    'Customer complaints',
    'Order entry error',
    'Customer changed mind',
] as const;

/** REF-6 — new. Every one of these returns a fee, never menu value. */
export const FEE_REFUND_REASONS = [
    'Tax exempt guest',
    'Fee charged in error',
    'Tip entry error',
    'Gratuity removed on request',
    'Service recovery',
] as const;

export const REASON_GROUP_LABEL: Record<RefundReasonGroup, string> = {
    item: 'Item & order reasons',
    fee: 'Fee reasons',
};

/**
 * Case-insensitive so this matches regardless of the source's own casing convention — the
 * catalogue above is sentence case ("Fee charged in error") while the real `/refund-log`
 * endpoint sends Title Case ("Fee Charged in Error"). Comparing case-sensitively would
 * silently misclassify every real fee reason as "item".
 */
const FEE_SET: ReadonlySet<string> = new Set<string>(FEE_REFUND_REASONS.map((r) => r.toLowerCase()));

/**
 * Classify a reason string coming off the API, which has no group field of its own.
 * Anything unrecognised falls to `item`: an unknown reason on an order is far more likely
 * to be a quality reason, and mis-filing one into the fee group would fabricate a control
 * signal that nobody can trace back to a fee.
 */
export function reasonGroupOf(reason: string | null | undefined): RefundReasonGroup {
    return FEE_SET.has(String(reason ?? '').trim().toLowerCase()) ? 'fee' : 'item';
}
